import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Phone, 
  Shield, 
  Award, 
  Users, 
  Target,
  CheckCircle,
  Star
} from "lucide-react";

const images = {
  team: "https://images.unsplash.com/photo-1681049400158-0ff6249ac315?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxyb29mZXIlMjByZXBhaXJpbmclMjByb29mJTIwc2FmZXR5JTIwZ2VhcnxlbnwwfHx8fDE3NzI3OTIwNjJ8MA&ixlib=rb-4.1.0&q=85",
  work: "https://images.unsplash.com/photo-1635424709845-3a85ad5e1f5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxyb29mJTIwcmVwYWlyJTIwd29ya2VyJTIwc2hpbmdsZXN8ZW58MHx8fHwxNzcyNzkyMTIxfDA&ixlib=rb-4.1.0&q=85",
  worker: "https://images.unsplash.com/photo-1633759593085-1eaeb724fc88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxyb29mJTIwcmVwYWlyJTIwd29ya2VyJTIwc2hpbmdsZXN8ZW58MHx8fHwxNzcyNzkyMTIxfDA&ixlib=rb-4.1.0&q=85",
};

const stats = [
  { value: "15+", label: "Years Experience" },
  { value: "5000+", label: "Roofs Repaired" },
  { value: "98%", label: "Customer Satisfaction" },
  { value: "24/7", label: "Emergency Service" },
];

const values = [
  {
    icon: Shield,
    title: "Integrity",
    description: "We provide honest assessments and fair pricing. No surprises, no hidden fees."
  },
  {
    icon: Award,
    title: "Excellence",
    description: "We use premium materials and proven techniques for lasting results."
  },
  {
    icon: Users,
    title: "Customer First",
    description: "Your satisfaction is our priority. We're not done until you're happy."
  },
  {
    icon: Target,
    title: "Reliability",
    description: "We show up on time, every time, and stand behind our work."
  }
];

const certifications = [
  "California Contractors State License Board (CSLB) Licensed",
  "Fully Bonded and Insured",
  "GAF Certified Contractor",
  "CertainTeed SELECT ShingleMaster",
  "OSHA Safety Certified",
  "BBB A+ Rated Business"
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0F172A]">
        <div className="absolute inset-0 opacity-20">
          <img src={images.team} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl animate-fadeInUp">
            <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">About Us</span>
            <h1 className="heading-h1 text-white mt-2 mb-6">YOUR TRUSTED ROOFING PARTNER</h1>
            <p className="text-slate-300 text-lg">
              For over 15 years, Cart Genix has been protecting Los Angeles homes and businesses 
              with expert roofing services. We're more than contractors – we're your neighbors.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#F97316]">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-4xl md:text-5xl font-black text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {stat.value}
                </p>
                <p className="text-white/80 uppercase tracking-wider text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-light" data-testid="about-story">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fadeInUp">
              <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Our Story</span>
              <h2 className="heading-h2 text-slate-900 mt-2 mb-6">BUILT ON TRUST, DRIVEN BY EXCELLENCE</h2>
              <div className="space-y-4 text-body">
                <p>
                  Cart Genix was founded in 2009 with a simple mission: provide honest, reliable roofing 
                  services at fair prices. What started as a small family operation has grown into one of 
                  Los Angeles' most trusted roofing companies.
                </p>
                <p>
                  Our founder, a third-generation roofer, built this company on the values he learned from 
                  his father and grandfather – integrity, craftsmanship, and treating every customer's home 
                  as if it were our own.
                </p>
                <p>
                  Today, our team of certified professionals continues this legacy, combining time-tested 
                  techniques with modern technology to deliver exceptional results on every project.
                </p>
              </div>
              <Link to="/contact" className="mt-8 inline-block">
                <Button className="btn-primary">
                  Get Your Free Quote
                </Button>
              </Link>
            </div>
            
            <div className="relative animate-fadeInUp delay-200">
              <img 
                src={images.work} 
                alt="Cart Genix team at work" 
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-2">
                    {[1,2,3].map((i) => (
                      <div key={i} className="w-10 h-10 bg-slate-200 rounded-full border-2 border-white"></div>
                    ))}
                  </div>
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[1,2,3,4,5].map((i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-sm text-slate-600">500+ 5-Star Reviews</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-gray" data-testid="about-values">
        <div className="container-custom">
          <div className="text-center mb-16 animate-fadeInUp">
            <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Our Values</span>
            <h2 className="heading-h2 text-slate-900 mt-2">WHAT WE STAND FOR</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={value.title} 
                className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 text-center animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#F97316]" />
                </div>
                <h3 className="heading-h3 text-slate-900 mb-3">{value.title}</h3>
                <p className="text-body">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="section-padding bg-dark" data-testid="about-certifications">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fadeInUp">
              <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Credentials</span>
              <h2 className="heading-h2 text-white mt-2 mb-6">LICENSED, BONDED & CERTIFIED</h2>
              <p className="text-slate-300 mb-8">
                Your home deserves the best protection. That's why we maintain the highest 
                industry certifications and insurance coverage for your peace of mind.
              </p>
              
              <ul className="space-y-4">
                {certifications.map((cert) => (
                  <li key={cert} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#F97316] flex-shrink-0" />
                    <span className="text-white">{cert}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="animate-fadeInUp delay-200">
              <img 
                src={images.worker} 
                alt="Certified roofing professional" 
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#F97316]">
        <div className="container-custom text-center">
          <h2 className="heading-h2 text-white mb-4">READY TO WORK WITH US?</h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Experience the Cart Genix difference. Contact us today for a free inspection 
            and see why thousands of LA homeowners trust us with their roofs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-[#F97316] hover:bg-slate-100 font-bold h-12 px-8 rounded-md shadow-lg">
                Get Free Estimate
              </Button>
            </Link>
            <a href="tel:+18884451244">
              <Button className="bg-[#0F172A] hover:bg-slate-800 text-white font-bold h-12 px-8 rounded-md shadow-lg">
                <Phone className="w-4 h-4" />
                Call: 888-445-1244
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
